import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {gateTooltip} from './gate-tooltip'
import {useCloseAssessment, type SgsstAssessment} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {formatRatePct} from '@/lib/risk'

export function CloseControls({
  businessId,
  assessment,
  canAttest,
  firmaBlockedReason,
}: {
  businessId: string
  assessment: SgsstAssessment
  canAttest: boolean
  firmaBlockedReason: string | null
}) {
  const {t} = useTranslation()
  const closeMutation = useCloseAssessment(businessId)
  const [confirming, setConfirming] = useState(false)

  if (!assessment.can_close)
    return (
      <p className="text-xs text-muted-foreground">
        {t('sgsst.cycle.closesInDecember', {year: assessment.period_year})}
      </p>
    )

  return (
    <>
      <Button
        size="sm"
        disabled={!canAttest}
        title={gateTooltip(canAttest, firmaBlockedReason, t)}
        onClick={() => setConfirming(true)}>
        {t('sgsst.cycle.close', {year: assessment.period_year})}
      </Button>
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('sgsst.cycle.confirmTitle', {year: assessment.period_year})}
        description={
          t('sgsst.cycle.confirmBody', {
            year: assessment.period_year,
            next: assessment.period_year + 1,
            pct: formatRatePct(assessment.snapshot_pct ?? assessment.score.total_pct),
          }) +
          (assessment.pending_sugerencias_count > 0
            ? ` ${t('marcado.close.pendingCheckpoint', {count: assessment.pending_sugerencias_count})}`
            : '')
        }
        confirmLabel={t('sgsst.cycle.confirmAction', {year: assessment.period_year})}
        cancelLabel={t('common.cancel')}
        pending={closeMutation.isPending}
        onConfirm={() => closeMutation.mutate(undefined, {onSuccess: () => setConfirming(false)})}
        porque={{
          label: t('sgsst.cycle.porqueLabel'),
          testId: 'sellar-porque',
          contenidoTestId: 'sellar-porque-contenido',
          contenido: (
            <ul className="list-disc space-y-1 pl-4">
              <li>{t('sgsst.cycle.porqueCongela', {year: assessment.period_year})}</li>
              <li>{t('sgsst.cycle.porqueReevalua', {next: assessment.period_year + 1})}</li>
              <li>
                {t('sgsst.cycle.porqueEvidencias', {
                  year: assessment.period_year,
                  next: assessment.period_year + 1,
                })}
              </li>
              <li>{t('sgsst.cycle.porqueAcciones', {next: assessment.period_year + 1})}</li>
              <li>{t('sgsst.cycle.porqueMinisterio')}</li>
            </ul>
          ),
        }}
      />
    </>
  )
}
